
class InfiniteScroll {
  constructor(container) {
    this.container = container;
    this.grid = container.querySelector('#product-grid');
    this.pagination = container.querySelector('.wt-pagination-wrapper');
    this.loading = false;
    this.hasMorePages = false;
    this.nextPageUrl = null;
    this.loadingDiv = container.querySelector('.infinite-scroll-loading');
    if (this.loadingDiv) this.loadingDiv.style.display = 'none'; // Ensure hidden initially
    this.loadCount = 0; // Contador de cargas
    this.expectedProductsPerPage = this.getExpectedProductsPerPage();
    
    // Variables para la precarga
    this.preloading = false;
    this.preloadedPages = []; // Array de páginas precargadas: [{products: [], nextUrl: '', imagesLoaded: true/false, pageNumber: N, url: ''}]
    this.initialPreloadComplete = false; // Flag para saber si la precarga inicial está completa
    this.currentPageNumber = 1; // Número de página actual (la primera página ya está cargada)
    this.preloadedUrls = new Set(); // Set para rastrear URLs ya precargadas
    this.displayedPageNumbers = new Set([1]); // Set para rastrear números de página ya mostrados
    this.waitingForImages = false; // Flag para saber si estamos esperando a que se carguen imágenes
    this.currentlyShowingPage = null; // Número de página que se está mostrando actualmente
    this.lastDisplayedPage = 1; // Última página que se mostró al usuario (para control de flujo)
    this.checkBufferInterval = null; // Intervalo para verificar el buffer periódicamente
    this.targetBufferSize = 2; // Nº de páginas a mantener precargadas por delante del scroll

    this.init();
  }

  getExpectedProductsPerPage() {
    // Intentar obtener el valor configurado desde el settings del theme
    // Por defecto, asumimos 8 si no lo encontramos
    return 8;
  }

  countVisibleProducts() {
    // Contar productos visibles (sin la clase product-out-of-stock)
    const allProducts = this.grid.querySelectorAll('.collection__grid__item');
    let visibleCount = 0;

    allProducts.forEach(product => {
      // Verificar si el producto está visible (no tiene display: none)
      const computedStyle = window.getComputedStyle(product);
      if (computedStyle.display !== 'none') {
        visibleCount++;
      }
    });

    return visibleCount;
  }

  /**
   * Sincroniza hasMorePages en el grid para que collection-grid-layout detecte fin de paginación
   */
  syncHasMorePagesToGrid() {
    if (!this.grid) return;
    this.grid.dataset.infiniteScrollHasMorePages = this.hasMorePages ? 'true' : 'false';
  }

  init() {
    if (!this.grid) {
      console.warn('InfiniteScroll: Grid not found');
      return;
    }

    if (!this.pagination) {
      console.warn('InfiniteScroll: Pagination not found');
      return;
    }

    // Buscar el botón "Siguiente" en la paginación
    this.findNextPageUrl();
    this.syncHasMorePagesToGrid();

    if (this.hasMorePages) {
      // Ocultar paginación tradicional
      this.pagination.style.display = 'none';

      // Configurar Intersection Observer para scroll infinito
      this.setupObserver();

      // Verificar al cargar si hay productos ocultos y necesitamos cargar más
      setTimeout(() => {
        this.checkAndLoadMoreIfNeeded();
      }, 500);

      // Iniciar precarga inicial (página siguiente)
      setTimeout(() => {
        this.initialPreload();
      }, 1000);
    }
  }

  findNextPageUrl() {
    // Buscar el botón "Siguiente"
    // en la paginación de Brownie, el botón "prev" es el que va a la SIGUIENTE página
    let nextLink = this.pagination.querySelector('a.wt-pagination__item--prev');

    // Si no lo encuentra, intentar otros selectores
    if (!nextLink) {
      nextLink = this.pagination.querySelector(
        '.wt-pagination__item--next a, ' +
        '.pagination__item--next a, ' +
        '.pagination__next a, ' +
        'a[rel="next"]'
      );
    }

    if (nextLink && nextLink.href) {
      this.nextPageUrl = nextLink.href;
      this.hasMorePages = true;
    }
  }

  setupObserver() {
    // Usar scroll event en lugar de Intersection Observer para mayor control
    let lastScrollTop = 0;
    let ticking = false;

    const checkScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;

      // Obtener la posición del final del grid
      const gridRect = this.grid.getBoundingClientRect();
      const gridBottom = gridRect.bottom + scrollTop;
      const distanceFromGrid = gridBottom - (scrollTop + windowHeight);

      // Calcular la distancia de trigger basada en la altura de una fila de cards
      const triggerDistance = this.calculateTriggerDistance();

      // Cargar cuando esté a la distancia calculada del final del grid, scrolleando hacia abajo y no esté ya cargando
      if (distanceFromGrid < triggerDistance &&
        !this.loading &&
        this.hasMorePages &&
        scrollTop > lastScrollTop &&
        scrollTop > 100) { // Asegurar que el usuario hizo scroll

        this.loadMore(true);
      }

      lastScrollTop = scrollTop;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          checkScroll();
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    // Guardar referencia para poder limpiarlo después
    this.scrollHandler = onScroll;
  }

  /**
   * Calcula la distancia de trigger basada en la altura de las cards
   * Objetivo: activar el trigger aproximadamente 1 fila antes del final
   */
  calculateTriggerDistance() {
    // Obtener todas las cards visibles
    const cards = this.grid.querySelectorAll('.collection__grid__item');
    
    if (cards.length === 0) {
      // Fallback: 600px (aproximadamente 1.5 filas en móvil)
      return 600;
    }

    // Calcular la altura promedio de las primeras cards (más representativas)
    let totalHeight = 0;
    let count = 0;
    const samplesToCheck = Math.min(4, cards.length); // Verificar hasta 4 cards

    for (let i = 0; i < samplesToCheck; i++) {
      const card = cards[i];
      const computedStyle = window.getComputedStyle(card);
      
      // Solo contar cards visibles
      if (computedStyle.display !== 'none') {
        const height = card.offsetHeight;
        if (height > 0) {
          totalHeight += height;
          count++;
        }
      }
    }

    if (count === 0) {
      // Fallback si no se pudo calcular
      return 600;
    }

    // Altura promedio de una card
    const avgCardHeight = totalHeight / count;

    // Obtener el número de columnas del grid
    const gridColumns = this.getGridColumns();

    // Calcular altura de una fila (altura de card + gap)
    // Obtener el gap del grid
    const gridStyle = window.getComputedStyle(this.grid);
    const gap = parseInt(gridStyle.gap || gridStyle.rowGap || '16', 10);

    const rowHeight = avgCardHeight + gap;

    // Trigger a 1.5 filas antes del final (para dar margen)
    const triggerDistance = rowHeight * 1.5;

    console.log(`InfiniteScroll: 📏 Trigger calculado - Card: ${Math.round(avgCardHeight)}px, Fila: ${Math.round(rowHeight)}px, Trigger: ${Math.round(triggerDistance)}px`);

    // Asegurar un mínimo de 400px y máximo de 1200px
    return Math.max(400, Math.min(1200, triggerDistance));
  }

  /**
   * Obtiene el número de columnas del grid
   */
  getGridColumns() {
    const dataColumns = this.grid.getAttribute('data-columns');
    if (dataColumns) {
      return parseInt(dataColumns, 10);
    }

    // Fallback: detectar por el ancho de las cards
    const cards = this.grid.querySelectorAll('.collection__grid__item');
    if (cards.length >= 2) {
      const firstCard = cards[0];
      const secondCard = cards[1];
      
      // Si están en la misma fila (mismo offsetTop), hay al menos 2 columnas
      if (Math.abs(firstCard.offsetTop - secondCard.offsetTop) < 10) {
        // Contar cuántas cards están en la primera fila
        let columnsInFirstRow = 0;
        const firstRowTop = firstCard.offsetTop;
        
        for (const card of cards) {
          if (Math.abs(card.offsetTop - firstRowTop) < 10) {
            columnsInFirstRow++;
          } else {
            break;
          }
        }
        
        return columnsInFirstRow || 2;
      }
    }

    // Fallback: 2 columnas
    return 2;
  }

  async loadMore() {
    if (this.loading) {
      console.log('InfiniteScroll: ⚠️ loadMore ya en ejecución, saltando...');
      return;
    }
    if (this.waitingForImages) {
      console.log('InfiniteScroll: ⚠️ Esperando imágenes, saltando loadMore...');
      return;
    }
    if (!this.hasMorePages) return;

    this.loading = true;

    try {
      // Calcular cuál es la siguiente página que se debe mostrar
      const expectedNextPage = this.lastDisplayedPage + 1;
      
      console.log(`InfiniteScroll: Última página mostrada: ${this.lastDisplayedPage}, Esperando página: ${expectedNextPage}`);
      console.log(`InfiniteScroll: Páginas en buffer: [${this.preloadedPages.map(p => p.pageNumber).join(', ')}]`);
      
      // Si hay páginas precargadas, verificar si alguna es la que toca mostrar
      if (this.preloadedPages.length > 0) {
        
        // Buscar si la página esperada está en el buffer
        const expectedPageIndex = this.preloadedPages.findIndex(page => page.pageNumber === expectedNextPage);
        
        if (expectedPageIndex === -1) {
          // La página esperada no está en el buffer
          console.log(`InfiniteScroll: ⏳ PÁGINA ${expectedNextPage} no está en el buffer, esperando...`);
          
          // Mostrar loading indicator
          if (this.loadingDiv) {
            this.loadingDiv.style.display = 'block';
          }
          
          // Iniciar polling para verificar cuando la página esté lista
          this.startBufferCheck();
          
          this.loading = false;
          
          return;
        }
        
        // La página esperada está en el buffer
        const expectedPage = this.preloadedPages[expectedPageIndex];
        
        console.log(`InfiniteScroll: ✅ PÁGINA ${expectedNextPage} encontrada en buffer (posición ${expectedPageIndex})`);
        
        // Verificar si ya estamos mostrando esta página
        if (this.currentlyShowingPage === expectedPage.pageNumber) {
          console.log(`InfiniteScroll: ⚠️ Ya estamos procesando PÁGINA ${expectedPage.pageNumber}, saltando...`);
          this.loading = false;
          return;
        }
        
        // Verificar si las imágenes están cargadas
        if (expectedPage.imagesLoaded) {
          // Verificar si esta página ya fue mostrada (doble verificación)
          if (this.displayedPageNumbers.has(expectedPage.pageNumber)) {
            console.log(`InfiniteScroll: ⚠️ PÁGINA ${expectedPage.pageNumber} ya fue mostrada, removiendo del buffer...`);
            this.preloadedPages.splice(expectedPageIndex, 1); // Remover del buffer
            this.loading = false;
            // Intentar mostrar la siguiente página
            setTimeout(() => this.loadMore(), 100);
            return;
          }

          // Marcar que estamos mostrando esta página
          this.currentlyShowingPage = expectedPage.pageNumber;
          
          // Detener cualquier polling activo
          this.stopBufferCheck();
          
          // Ocultar loading indicator ya que vamos a mostrar la página
          if (this.loadingDiv) {
            this.loadingDiv.style.display = 'none';
          }

          console.log(`InfiniteScroll: ✅ Mostrando PÁGINA ${expectedPage.pageNumber} precargada - ${expectedPage.products.length} productos (imágenes ya cargadas)`);
          
          // Marcar página como mostrada y actualizar última página mostrada
          this.displayedPageNumbers.add(expectedPage.pageNumber);
          this.lastDisplayedPage = expectedPage.pageNumber;
          
          // Mostrar los productos precargados
          expectedPage.products.forEach((product, index) => {
            product.style.display = '';
            product.classList.remove('preloaded-hidden');
            // Si el usuario ya había activado la vista alternativa (vista-2 mobile /
            // vista-4 desktop) ANTES de que este producto precargado se revelara, nada
            // más vuelve a comprobar su visibilidad: connectedCallback() ya corrió hace
            // rato (cuando aún estaba display:none, oculto por precarga, no por la vista),
            // y 'collection:viewToggled' no se dispara de nuevo solo por revelar un
            // producto. Sin esto, su galería alternativa se quedaba con data-src para
            // siempre aunque ya tocara mostrarse. instantiateIn primero: si la galería
            // alternativa de este producto sigue como <template> (nunca instanciada),
            // conviértela en real antes de intentar hidratarla — sin parpadeo, ya que
            // ocurre en el mismo momento en que el producto se revela.
            if (window.altGalleryHelpers) window.altGalleryHelpers.instantiateIn(product);
            product.querySelectorAll('card-gallery').forEach((gallery) => {
              if (gallery._hydrateFirstSlideIfVisible) gallery._hydrateFirstSlideIfVisible();
            });
            console.log(`  Producto ${index + 1} mostrado`);
          });

          // Actualizar URL de siguiente página y hasMorePages ANTES del evento
          // para que collection-grid-layout tenga el estado correcto al detectar orphans
          const lastInBuffer = this.preloadedPages[this.preloadedPages.length - 1];
          const pageToUse = (lastInBuffer === expectedPage) ? expectedPage : lastInBuffer;
          if (pageToUse?.nextUrl) {
            this.nextPageUrl = pageToUse.nextUrl;
            this.hasMorePages = true;
          } else {
            this.hasMorePages = false;
            // No se quita el listener de scroll aquí: checkScroll() ya comprueba
            // this.hasMorePages antes de intentar cargar más páginas, así que quitarlo no
            // aporta nada a la paginación.
          }
          this.syncHasMorePagesToGrid();

          // Remover la página mostrada del array
          this.preloadedPages.splice(expectedPageIndex, 1);
          this.currentlyShowingPage = null;
          
          // Trigger evento para que otros scripts sepan que se mostraron productos
          window.dispatchEvent(new CustomEvent('infiniteScroll:loaded', {
            detail: { products: expectedPage.products }
          }));

          console.log('InfiniteScroll: Siguiente URL para precargar:', this.nextPageUrl);
          console.log('InfiniteScroll: Páginas en buffer después de mostrar:', this.preloadedPages.length);
          console.log('InfiniteScroll: ¿Hay más páginas?:', this.hasMorePages);
          
          // Calcular cuántas páginas necesitamos en el buffer
          // El buffer debe tener targetBufferSize páginas adelante de la última mostrada
          const nextExpectedPage = this.lastDisplayedPage + 1;
          const targetBufferSize = this.targetBufferSize;
          
          // Contar cuántas páginas tenemos en el buffer (incluyendo las que están cargando)
          const pagesInBuffer = this.preloadedPages.length;
          const isPreloading = this.preloading;
          
          console.log(`InfiniteScroll: 📊 Estado buffer - En buffer: ${pagesInBuffer}, Precargando: ${isPreloading}, Objetivo: ${targetBufferSize}, Siguiente esperada: ${nextExpectedPage}`);
          
          // Verificar que las páginas en el buffer sean las correctas (consecutivas desde nextExpectedPage)
          const correctPagesInBuffer = this.preloadedPages.filter(page => 
            page.pageNumber >= nextExpectedPage && page.pageNumber < nextExpectedPage + targetBufferSize
          ).length;
          
          console.log(`InfiniteScroll: 📊 Páginas correctas en buffer: ${correctPagesInBuffer}/${targetBufferSize}`);
          
          // Calcular cuántas páginas faltan para completar el buffer
          const pagesNeeded = targetBufferSize - correctPagesInBuffer - (isPreloading ? 1 : 0);
          
          if (pagesNeeded > 0 && this.hasMorePages && this.nextPageUrl && !isPreloading) {
            console.log(`InfiniteScroll: 🔄 Faltan ${pagesNeeded} página(s) para completar buffer, iniciando precarga...`);
            setTimeout(() => {
              this.preloadNextPage();
            }, 500);
          } else {
            if (!this.hasMorePages) {
              console.log('InfiniteScroll: ✅ No hay más páginas, no se precarga');
            } else if (correctPagesInBuffer >= targetBufferSize) {
              console.log(`InfiniteScroll: ✅ Buffer completo (${targetBufferSize} página(s) correctas), no se precarga más`);
            } else if (isPreloading) {
              console.log('InfiniteScroll: ⏳ Ya hay una precarga en progreso');
            } else if (!this.nextPageUrl) {
              console.log('InfiniteScroll: ⚠️ No hay URL para precargar');
            }
          }

          // Verificar si hay suficientes productos visibles
          setTimeout(() => {
            this.checkAndLoadMoreIfNeeded();
          }, 500);

        } else {
          // La página esperada está en el buffer pero las imágenes no están listas, esperar
          console.log('InfiniteScroll: ⏳ Esperando a que terminen de cargar las imágenes de PÁGINA', expectedPage.pageNumber);
          
          // Marcar que estamos esperando imágenes
          this.waitingForImages = true;
          
          // Mostrar loading indicator mientras esperamos
          if (this.loadingDiv) {
            this.loadingDiv.style.display = 'block';
          }

          // Iniciar polling para verificar cuando las imágenes estén listas
          this.startBufferCheck();

          // Reintentar después de un momento (backup por si el polling falla)
          setTimeout(() => {
            this.waitingForImages = false;
            this.loading = false;
            this.loadMore();
          }, 1000);
          
          // Salir inmediatamente sin ejecutar el finally
          return;
        }

      } else {
        // No hay páginas precargadas en el buffer - esperar a que termine la precarga en curso
        console.log('InfiniteScroll: ⏳ Buffer vacío, esperando a que termine la precarga...');
        
        // Mostrar loading indicator
        if (this.loadingDiv) {
          this.loadingDiv.style.display = 'block';
        }
        
        // Iniciar polling para verificar cuando haya páginas en el buffer
        this.startBufferCheck();
        
        this.loading = false;
        
        return; // Salir sin ejecutar el finally
      }
    } catch (error) {
      console.error('Error loading more products:', error);
      if (this.loadingDiv) {
        this.loadingDiv.style.display = 'none';
      }
      this.loading = false;
    } finally {
      // Solo limpiar loading si no estamos esperando imágenes o buffer vacío
      // (esos casos ya manejan su propio loading state)
      if (!this.waitingForImages) {
        this.loading = false;
      }
    }
  }

  getLoadMoreText() {
    return document.documentElement.lang === 'es' ? 'Cargar más' : 'Load more';
  }

  getLoadingText() {
    return document.documentElement.lang === 'es' ? 'Cargando...' : 'Loading...';
  }

  getErrorText() {
    return document.documentElement.lang === 'es' ? 'Error al cargar' : 'Error loading';
  }

  checkAndLoadMoreIfNeeded() {
    if (!this.hasMorePages || this.loading) return;

    const visibleCount = this.countVisibleProducts();

    // Cargar más si la página se queda corta, sin importar el motivo: agotados
    // ocultos vía CSS (dejan rastro en el DOM) o productos ocultos por mercado
    // (no dejan ningún nodo, así que no hay "hiddenCount" que detectar).
    if (visibleCount < this.expectedProductsPerPage) {
      console.log(`InfiniteScroll: solo ${visibleCount} producto(s) visibles de ${this.expectedProductsPerPage} esperados. Cargando más...`);
      this.loadMore();
    }
  }

  /**
   * Inicia un polling para verificar si la página esperada está lista en el buffer
   */
  startBufferCheck() {
    // Limpiar cualquier intervalo anterior
    this.stopBufferCheck();

    console.log('InfiniteScroll: 🔄 Iniciando verificación periódica del buffer...');

    // Verificar cada 500ms si la página está lista
    this.checkBufferInterval = setInterval(() => {
      const expectedNextPage = this.lastDisplayedPage + 1;
      
      // Buscar si la página esperada está en el buffer
      const expectedPageIndex = this.preloadedPages.findIndex(page => page.pageNumber === expectedNextPage);
      
      if (expectedPageIndex !== -1) {
        const expectedPage = this.preloadedPages[expectedPageIndex];
        
        // Verificar si las imágenes están cargadas
        if (expectedPage.imagesLoaded && !this.loading) {
          console.log(`InfiniteScroll: ✅ Página ${expectedNextPage} lista en buffer, intentando mostrar...`);
          
          // Detener el polling
          this.stopBufferCheck();
          
          // Ocultar el loading
          if (this.loadingDiv) {
            this.loadingDiv.style.display = 'none';
          }
          
          // Intentar cargar la página
          this.loadMore();
        } else {
          console.log(`InfiniteScroll: ⏳ Página ${expectedNextPage} en buffer pero imágenes no listas (${expectedPage.imagesLoaded ? 'cargadas' : 'cargando'}), esperando...`);
        }
      } else {
        console.log(`InfiniteScroll: ⏳ Página ${expectedNextPage} aún no está en buffer, esperando...`);
      }
    }, 500);
  }

  /**
   * Detiene el polling de verificación del buffer
   */
  stopBufferCheck() {
    if (this.checkBufferInterval) {
      clearInterval(this.checkBufferInterval);
      this.checkBufferInterval = null;
      console.log('InfiniteScroll: ⏹️ Verificación periódica del buffer detenida');
    }
  }

  async initialPreload() {
    if (this.initialPreloadComplete) return;
    if (!this.hasMorePages || !this.nextPageUrl) return;

    console.log('InfiniteScroll: 🚀 Iniciando precarga inicial (página 2)...');

    try {
      // Precargar página 2 (única página que mantenemos por delante del scroll)
      await this.preloadPage(this.nextPageUrl);

      this.initialPreloadComplete = true;
      console.log('InfiniteScroll: ✅ Precarga inicial completa -', this.preloadedPages.length, 'páginas listas');
    } catch (error) {
      console.error('Error en precarga inicial:', error);
    }
  }

  async preloadPage(url) {
    if (!url) {
      console.log('InfiniteScroll: ⚠️ URL vacía, no se puede precargar');
      return;
    }

    // Verificar si esta URL ya fue precargada
    if (this.preloadedUrls.has(url)) {
      console.log('InfiniteScroll: ⚠️ URL ya precargada, saltando:', url);
      return;
    }

    // Incrementar el número de página ANTES de marcar la URL
    // Esto asegura que el número de página sea correcto incluso si hay múltiples llamadas
    this.currentPageNumber++;
    const pageNumber = this.currentPageNumber;

    // Marcar URL como precargada inmediatamente para evitar duplicados
    this.preloadedUrls.add(url);

    console.log(`InfiniteScroll: Precargando PÁGINA ${pageNumber}:`, url);
    
    const response = await fetch(url);
    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Obtener nuevos productos
    const newProducts = doc.querySelectorAll('#product-grid .collection__grid__item');

    if (newProducts.length > 0) {
      // Filtrar banners duplicados: obtener posiciones de banners ya existentes en el grid
      const existingBannerPositions = new Set();
      this.grid.querySelectorAll('.collection-banner-item[data-banner-position]').forEach(el => {
        existingBannerPositions.add(el.getAttribute('data-banner-position'));
      });
      // También rastrear IDs de producto_duplicado ya existentes (banners tipo product_banner)
      const existingProductBannerIds = new Set();
      this.grid.querySelectorAll('.collection-banner-item[data-banner-type="product_banner"][data-id]').forEach(el => {
        existingProductBannerIds.add(el.getAttribute('data-id'));
      });

      // Array para guardar los productos precargados
      const preloadedProducts = [];

      // Agregar productos directamente al grid pero ocultos
      newProducts.forEach(product => {
        // Saltar banners duplicados (ya existen en el grid desde una página anterior)
        if (product.classList.contains('collection-banner-item')) {
          const bannerPos = product.getAttribute('data-banner-position');
          const bannerType = product.getAttribute('data-banner-type');
          const bannerId = product.getAttribute('data-id');

          if (bannerPos && existingBannerPositions.has(bannerPos)) {
            console.log(`InfiniteScroll: ⚠️ Banner duplicado en posición ${bannerPos} filtrado (ya existe en el grid)`);
            return; // Skip this item
          }
          if (bannerType === 'product_banner' && bannerId && existingProductBannerIds.has(bannerId)) {
            console.log(`InfiniteScroll: ⚠️ Product banner duplicado (id: ${bannerId}) filtrado (ya existe en el grid)`);
            return; // Skip this item
          }
        }

        const clonedProduct = product.cloneNode(true);
        // Agregar clase para ocultarlos
        clonedProduct.classList.add('preloaded-hidden');
        clonedProduct.style.display = 'none';
        
        // Insertar en el grid
        this.grid.appendChild(clonedProduct);
        
        // Guardar referencia
        preloadedProducts.push(clonedProduct);

        // No forzamos la carga de las imágenes aquí: mientras el producto está
        // oculto (display:none) no tiene caja de layout, así que el navegador
        // no las descarga. Se cargarán de forma nativa (respetando su loading="lazy"
        // y con el "sizes" resuelto contra el viewport real, no el de escritorio)
        // en el momento en que se revelen en loadMore().
      });
      
      // Buscar la URL de la siguiente página
      let nextUrl = null;
      const newPagination = doc.querySelector('.wt-pagination-wrapper');
      if (newPagination) {
        let nextLink = newPagination.querySelector('a.wt-pagination__item--prev');

        if (!nextLink) {
          nextLink = newPagination.querySelector(
            '.wt-pagination__item--next a, ' +
            '.pagination__item--next a, ' +
            '.pagination__next a, ' +
            'a[rel="next"]'
          );
        }

        if (nextLink && nextLink.href) {
          nextUrl = nextLink.href;
        }
      }

      // Verificar que no exista ya esta página en el buffer
      const alreadyInBuffer = this.preloadedPages.some(page => page.pageNumber === pageNumber);
      if (alreadyInBuffer) {
        console.log(`InfiniteScroll: ⚠️ PÁGINA ${pageNumber} ya está en el buffer, no se agrega`);
        return;
      }

      // Agregar página al array de páginas precargadas
      this.preloadedPages.push({
        products: preloadedProducts,
        nextUrl: nextUrl,
        imagesLoaded: true,
        pageNumber: pageNumber,
        url: url
      });

      console.log(`InfiniteScroll: PÁGINA ${pageNumber} precargada lista - ${preloadedProducts.length} productos`);
      console.log(`InfiniteScroll: Total páginas en buffer: ${this.preloadedPages.length}`);
      
      // Si el buffer tiene menos páginas de las necesarias y hay más páginas disponibles, precargar otra
      if (this.preloadedPages.length < this.targetBufferSize && nextUrl && this.hasMorePages) {
        console.log(`InfiniteScroll: Buffer tiene solo ${this.preloadedPages.length} página(s), precargando otra para completar buffer de ${this.targetBufferSize}...`);
        // Actualizar nextPageUrl para la siguiente precarga
        this.nextPageUrl = nextUrl;
        // Precargar la siguiente página después de un momento
        setTimeout(() => {
          this.preloadNextPage();
        }, 100);
      }
    }
  }

  async preloadNextPage() {
    // No precargar si ya estamos precargando, cargando, o no hay más páginas
    if (this.preloading || this.loading || !this.hasMorePages || !this.nextPageUrl) {
      if (this.preloading) {
        console.log('InfiniteScroll: ⚠️ Ya hay una precarga en progreso, saltando...');
      } else if (this.loading) {
        console.log('InfiniteScroll: ⚠️ loadMore en progreso, saltando precarga...');
      } else if (!this.hasMorePages) {
        console.log('InfiniteScroll: ✅ No hay más páginas, no se precarga');
      } else if (!this.nextPageUrl) {
        console.log('InfiniteScroll: ⚠️ No hay nextPageUrl, no se precarga');
      }
      return;
    }

    // Verificar si ya tenemos suficientes páginas en el buffer
    if (this.preloadedPages.length >= this.targetBufferSize) {
      console.log(`InfiniteScroll: ⚠️ Buffer completo (${this.targetBufferSize} página(s)), no se precarga más`);
      return;
    }

    console.log('InfiniteScroll: 🔄 Iniciando preloadNextPage()...');
    this.preloading = true;

    try {
      await this.preloadPage(this.nextPageUrl);
    } catch (error) {
      console.error('Error preloading next page:', error);
      // Remover la URL del set si falló
      this.preloadedUrls.delete(this.nextPageUrl);
    } finally {
      this.preloading = false;
    }
  }
}

// Variable para guardar la instancia actual
let infiniteScrollInstance = null;

// Función para inicializar el scroll infinito
function initInfiniteScroll() {
  let container = document.querySelector('collection-section');

  if (!container) {
    container = document.querySelector('section.collection');
  }

  if (!container) {
    const sections = document.querySelectorAll('[data-section-id]');

    // Buscar la sección que contiene el grid
    for (const section of sections) {
      const grid = section.querySelector('#product-grid');
      if (grid) {
        container = section;
        break;
      }
    }
  }

  if (container) {
    const grid = container.querySelector('#product-grid');
    const pagination = container.querySelector('.wt-pagination-wrapper');

    if (grid && pagination) {
      // Destruir instancia anterior si existe
      if (infiniteScrollInstance) {
        if (infiniteScrollInstance.scrollHandler) {
          window.removeEventListener('scroll', infiniteScrollInstance.scrollHandler);
        }
      }

      // Crear nueva instancia
      infiniteScrollInstance = new InfiniteScroll(container);
      console.log('InfiniteScroll: Iniciado');
    }
  }
}

// Inicializar cuando el DOM esté listo
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initInfiniteScroll);
} else {
  // DOM ya está listo
  initInfiniteScroll();
}

// Re-inicializar después de aplicar filtros
document.addEventListener('facets:change', () => {
  setTimeout(() => {
    console.log('InfiniteScroll: Reiniciado despues de cambios');
    initInfiniteScroll();
  }, 300);
});

